import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PageModal from '../common/PageModal'
import SimpleButton from '../common/SimpleButton'
import SearchWithFilter from '../common/SearchWithFilter'
import EmptyPageResults from '../common/EmptyPageResults'
import LoadingSpinner from '../common/LoadingSpinner'
import Dropdown from '../common/Dropdown'
import GenericTable from '../common/GenericTable'
import { loadBranches } from '../../actions/branches'
import countries from '../../utils/countries.json'
import { useWindowSize } from '../../libs/hooks'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'
import './companies-management-modal.scss'

const orderingFields = [
  {
    field: 'branch',
    order: 'asc',
    translationKey: 'branch_asc',
  },
  {
    field: 'branch',
    order: 'desc',
    translationKey: 'branch_desc',
  },
  {
    field: 'organizations',
    order: 'asc',
    translationKey: 'organization_asc',
  },
  {
    field: 'organizations',
    order: 'desc',
    translationKey: 'organization_desc',
  },
  {
    field: 'members',
    order: 'asc',
    translationKey: 'members_asc',
  },
  {
    field: 'members',
    order: 'desc',
    translationKey: 'members_desc',
  },
]

const AddExistingBranchModal = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { selectedBranches, accountType, handleUpdateBranches, closeModal, onSave, companyId } = props
  const [branchesTableData, setBranchesTableData] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [showSpinner, setShowSpinner] = useState(null)
  const [expandedBranch, setExpandedBranch] = useState(0)

  useEffect(() => {
    const tableData = props.branches.map((branch, index) => {
      return {
        index,
        identifier: branch.id,
        organization: branch.company?.name,
        branch: branch.branchName,
        country: branch.country,
        admin: branch.email,
        members: branch.members,
        devices: branch.totalDevices,
      }
    })

    setBranchesTableData(tableData)
  }, [props.branches])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadBranches(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        setSelectedPage(1)
        handleLoadBranches(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, orderBy])

  const handleSave = async () => {
    if (onSave) {
      await onSave(selectedBranches)
    }
    closeModal()
  }

  const handleLoadBranches = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      offset: withReset ? 0 : (selectedPage - 1) * process.env.REACT_APP_PAGINATION_SIZE,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
    }

    props.actions.loadBranches(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const createQueryParams = () => {
    let queryParams = {
      startPage: 1,
      account: accountType,
      isCorporation: false,
    }

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleRowClick = (branchId) => {
    const selectedBranch = props.branches.find((branch) => branch.id === branchId)
    const selectedBranchesIds = selectedBranches.map((branch) => branch.id)

    handleUpdateBranches(
      selectedBranchesIds.includes(selectedBranch.id)
        ? selectedBranches.filter((branch) => branch?.id !== selectedBranch.id)
        : [...selectedBranches, selectedBranch],
    )
  }

  return (
    <>
      <PageModal toggle onToggle={closeModal} className="companies-management-modal branches-selection-modal" title={t('organization_context.select_branches')}>
        <div className="companies-management-modal-content">
          <div className={`actions-wrapper${onSave ? ' save-btn' : ''}`}>
            <SearchWithFilter onSearch={(e) => handleSearch(e)} />
            {onSave && (
              <SimpleButton className="action-button" onClick={handleSave}>
                Save
              </SimpleButton>
            )}
          </div>
          {isMobile && branchesTableData.length > 0 && (
            <div className="order-by-mobile-wrapper">
              <Dropdown
                className="dropdown-input"
                placeholder={t('select_ordering')}
                options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
                initialSelectedKey={orderBy?.translationKey}
                onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
              />
            </div>
          )}
          {!isMobile && branchesTableData.length > 0 && (
            <GenericTable
              data={branchesTableData}
              headers={[
                {
                  title: t('organization'),
                  orderKey: 'organizations',
                },
                {
                  title: t('branch'),
                  orderKey: 'branch',
                },
                {
                  title: t('country'),
                },
                {
                  title: t('admin'),
                },
                {
                  title: t('nav.members'),
                  orderKey: 'members',
                },
                {
                  title: t('devices'),
                },
                {
                  title: '',
                },
              ]}
              keys={['organization', 'branch', 'country', 'admin', 'members', 'devices', 'action']}
              keyRenderer={{
                country: (item) => countries.find((country) => country.code === item.country)?.name,
                action: (item) => {
                  return (
                    <div
                      className="table-actions-wrapper"
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      <SimpleButton className="action-button" onClick={() => handleRowClick(item.identifier)}>
                        {selectedBranches.map((v) => v && v.id).includes(item.identifier) ? t('remove') : t('add')}
                      </SimpleButton>
                    </div>
                  )
                },
              }}
              onRowClick={(branch) => handleRowClick(branch.identifier)}
              activeSort={orderBy}
              onSort={(orderingField) => handleSort(orderingField)}
              isLoading={showSpinner}
            />
          )}
          {isMobile &&
            branchesTableData.map((branch, index) => (
              <div key={index} className="card" onClick={() => setExpandedBranch(index)}>
                <div className={'card-item' + (expandedBranch !== index ? ' align-center' : '')}>
                  <div className="card-item-title">{t('organization')}</div>
                  <div className={'card-item-body' + (expandedBranch !== index ? ' align-center' : '')}>
                    <div>{branch.organization}</div>
                    <div
                      className="card-actions"
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    ></div>
                  </div>
                </div>
                {expandedBranch === index && (
                  <>
                    <div className="card-item">
                      <div className="card-item-title">{t('branch')}</div>
                      <div className="card-item-body">{branch.branch}</div>
                    </div>
                    <div className="card-item">
                      <div className="card-item-title">{t('country')}</div>
                      <div className="card-item-body">{countries.find((country) => country.code === branch.country)?.name}</div>
                    </div>
                    <div className="card-item">
                      <div className="card-item-title">{t('admin')}</div>
                      <div className="card-item-body">{branch.admin}</div>
                    </div>
                    <div className="card-item">
                      <div className="card-item-title">{t('nav.members')}</div>
                      <div className="card-item-body">{branch.members}</div>
                    </div>
                    <div className="card-item">
                      <div className="card-item-title">{t('devices')}</div>
                      <div className="card-item-body">{branch.devices}</div>
                    </div>
                    <div className="card-item">
                      <SimpleButton className="action-button" onClick={() => handleRowClick(branch.identifier)}>
                        {selectedBranches.map((v) => v && v.id).includes(branch.identifier) ? t('remove') : t('add')}
                      </SimpleButton>
                    </div>
                  </>
                )}
              </div>
            ))}
          {!showSpinner && !isFirstLoad && branchesTableData.length === 0 && (
            <EmptyPageResults title={t('organization_context.no-branches-found')} subtitle={t('cannot_find_matching_search')} />
          )}
          {canLoadMore && (
            <div className="buttons-wrapper">
              <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
                {t('load_more')}
              </SimpleButton>
            </div>
          )}
        </div>
      </PageModal>
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
    </>
  )
}

function stateToProps({ branches }) {
  return {
    branches: branches?.branches || [],
    isLoading: branches?.isLoading,
    canLoadMore: branches?.canLoadMore,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadBranches,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddExistingBranchModal)

